import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Crown, Trophy, Gem, Loader2, Clock, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICON = { free: Zap, starter: Trophy, all_star: Crown };
const ANNUAL_PRICE = { starter: 50, all_star: 100 };

export default function ProTiers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [promo, setPromo] = useState(null);
  const [billing, setBilling] = useState("annual");
  const [loadingTier, setLoadingTier] = useState(null);

  useEffect(() => {
    api.get("/tiers").then((r) => setTiers(r.data.filter((t) => t.id !== "founder")));
    api.get("/payments/promo").then((r) => setPromo(r.data));
  }, []);

  const current = user?.role === "admin" ? "all_star" : user?.tier;

  const checkout = async (tierId) => {
    if (!user) { navigate("/login"); return; }
    if (tierId === "free") {
      await api.post("/me/tier?tier=free");
      toast.success("Switched to Rookie (free)");
      window.location.reload();
      return;
    }
    setLoadingTier(tierId);
    try {
      const { data } = await api.post("/payments/checkout",
        { tier: tierId, billing, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingTier(null);
    }
  };

  const priceFor = (t) => {
    if (t.price === 0) return { big: "Free", sub: "", note: null };
    if (billing === "annual") {
      const yr = ANNUAL_PRICE[t.id];
      return { big: `$${yr}`, sub: "/yr", strike: `$${t.price * 12}`, note: "2 months free" };
    }
    return { big: `$${t.price}`, sub: "/mo", note: null };
  };

  return (
    <div className="max-w-[1100px] mx-auto p-4 sm:p-6">
      <div className="text-center mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-[#00E5A3] mb-2">Membership</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50">Stop digging. Start knowing.</h1>
        <p className="text-slate-400 mt-3 max-w-2xl mx-auto">We surface the game-changing info you didn't know to look for — injuries, scratches, late changes across all four majors, cited and timestamped. Paid members just get it <span className="text-slate-200 font-semibold">first</span>.</p>
      </div>

      {/* billing toggle */}
      <div className="flex items-center justify-center gap-1 mb-8">
        <div className="inline-flex bg-[#0F1420] border border-[#232D42] rounded-lg p-1">
          <button data-testid="billing-monthly" onClick={() => setBilling("monthly")}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-[#1C2438] text-slate-100" : "text-slate-400 hover:text-slate-200"}`}>Monthly</button>
          <button data-testid="billing-annual" onClick={() => setBilling("annual")}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${billing === "annual" ? "bg-[#00E5A3] text-[#04140F]" : "text-slate-400 hover:text-slate-200"}`}>
            Annual <span className="font-mono text-xs opacity-80">–17%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {tiers.map((t) => {
          const Icon = ICON[t.id] || Zap;
          const top = t.id === "all_star";
          const isCurrent = current === t.id;
          const price = priceFor(t);
          return (
            <div key={t.id} data-testid={`tier-card-${t.id}`}
              className={`relative bg-[#0F1420] border rounded-2xl p-6 flex flex-col ${top ? "border-[#00E5A3]/60 shadow-[0_0_40px_-12px_rgba(0,229,163,0.4)]" : "border-[#232D42]"}`}>
              {top && <span className="absolute -top-3 left-6 rounded-full bg-[#00E5A3] px-3 py-0.5 text-xs font-bold text-[#04140F] font-mono uppercase tracking-wider">Most Popular</span>}
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} className={top ? "text-[#00E5A3]" : "text-slate-400"} />
                <h3 className="font-heading text-xl font-bold text-slate-100">{t.name}</h3>
              </div>
              <div className="flex items-end gap-1.5 mt-3 mb-1">
                {price.strike && <span className="font-mono text-sm text-slate-600 line-through mb-1.5">{price.strike}</span>}
                <span className="font-heading text-4xl font-extrabold text-slate-50">{price.big}</span>
                <span className="text-slate-500 mb-1.5 font-mono text-sm">{price.sub}</span>
              </div>
              {price.note ? <p className="text-xs text-[#00E5A3] font-mono mb-4 min-h-[16px]">{price.note}</p> : <div className="mb-4 min-h-[16px]" />}
              <ul className="space-y-3 mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check size={16} className={`${top ? "text-[#00E5A3]" : "text-slate-500"} mt-0.5 shrink-0`} />{f}
                  </li>
                ))}
              </ul>
              <Button data-testid={`tier-select-${t.id}`} onClick={() => checkout(t.id)} disabled={isCurrent || loadingTier === t.id}
                className={`w-full h-11 font-semibold rounded-lg ${top ? "bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F]" : "bg-[#151B2B] hover:bg-[#1C2438] text-slate-200 border border-[#232D42]"} ${isCurrent ? "opacity-60" : ""}`}>
                {loadingTier === t.id ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Redirecting…</>
                  : isCurrent ? "Current Plan"
                  : t.id === "free" ? "Downgrade to Free"
                  : `Get ${t.name}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Founder lifetime — scarcity */}
      {promo && (
        <div data-testid="founder-card" className="mt-6 rounded-2xl border border-[#FFB020]/50 bg-gradient-to-r from-[#FFB020]/10 via-[#151B2B] to-[#0F1420] p-6 sm:p-7">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Gem className="text-[#FFB020]" size={22} />
                <h3 className="font-heading text-2xl font-extrabold text-slate-50">Founder — Lifetime</h3>
                <span className="rounded-full bg-[#FFB020]/20 border border-[#FFB020]/40 px-2.5 py-0.5 text-xs font-mono uppercase tracking-wider text-[#FFB020]">First {promo.cap.toLocaleString()}</span>
              </div>
              <p className="text-slate-300 text-sm max-w-xl leading-relaxed">
                A one-time <span className="text-slate-100 font-semibold">$50</span> locks in <span className="text-slate-100 font-semibold">All-Star access forever</span> — real-time feed, priority alerts, biggest watchlists. Never pay again, even when prices rise. Founding Member badge included.
              </p>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1.5 text-slate-400"><Clock size={14} className="text-[#00E5A3]" /> Real-time</span>
                <span className="flex items-center gap-1.5 text-slate-400"><BellRing size={14} className="text-[#00E5A3]" /> Priority alerts</span>
                <span data-testid="founder-spots" className="font-mono text-[#FFB020]">{promo.spots_left.toLocaleString()} of {promo.cap.toLocaleString()} spots left</span>
              </div>
            </div>
            <div className="lg:w-64 text-center">
              <div className="flex items-end justify-center gap-1.5 mb-3">
                <span className="font-heading text-5xl font-extrabold text-slate-50">$50</span>
                <span className="text-slate-500 mb-2 font-mono text-sm">once</span>
              </div>
              <Button data-testid="tier-select-founder" onClick={() => checkout("founder")}
                disabled={promo.sold_out || loadingTier === "founder" || current === "founder"}
                className="w-full h-12 font-bold rounded-lg bg-[#FFB020] hover:bg-[#e6a017] text-[#1A1206]">
                {loadingTier === "founder" ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Redirecting…</>
                  : current === "founder" ? "You're a Founder 🎉"
                  : promo.sold_out ? "Sold Out"
                  : "Claim Lifetime Access"}
              </Button>
              <p className="text-[11px] text-slate-600 font-mono mt-2">10-month payment plan coming soon</p>
            </div>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-slate-600 font-mono mt-8">Secure checkout via Stripe · cancel anytime · SidelineIQ is an information tool, not betting advice.</p>
    </div>
  );
}
